import cuid from "cuid";
import { get, isNil } from "lodash";
import { useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";

const SILENT = { onError: () => undefined };

export const written = (result) => {
  const errors = get(result, "errors");
  if (!isNil(errors)) throw errors;
};

/**
 * The three ways markup reaches a screen, each welded to the detection that
 * has to follow it.
 *
 * Writing HTML and re-running slot detection is one indivisible step. A screen
 * left holding slots that point at markup it no longer contains is worse than
 * a screen with no slots at all: the bindings still look valid, and everything
 * downstream — the form, the runtime, the operator reading the list — keeps
 * believing them.
 *
 * Both cycles need that pairing. The correction cycle rewrites a target that
 * exists; the generation cycle also creates states that do not. Keeping one
 * copy of the pairing is the point: a second copy is a second chance to write
 * one half and ship it.
 */
const useScreenWrites = () => {
  const [updateVersion] = useMutation(
    graphql.mutations.UPDATE_SCREEN_VERSION,
    SILENT,
  );
  const [updateState] = useMutation(
    graphql.mutations.UPDATE_SCREEN_STATE,
    SILENT,
  );
  const [addState] = useMutation(graphql.mutations.ADD_SCREEN_STATE, SILENT);
  const [detectSlots] = useMutation(graphql.mutations.DETECT_SLOTS, SILENT);
  const [detectSlotsForState] = useMutation(
    graphql.mutations.DETECT_SLOTS_FOR_STATE,
    SILENT,
  );

  /**
   * Two callers want opposite things from an empty stylesheet, so the choice
   * is named rather than guessed.
   *
   * The model returns an empty sheet when it did not touch one: writing it
   * would blank the styles the screen already uses. An operator who empties
   * the CSS pane means exactly that. The flag defaults to the cautious
   * reading, so a caller who says nothing never destroys a stylesheet.
   */
  const writeVersion = async ({
    id,
    htmlContent,
    styleContent,
    keepEmptyStyle = false,
  }) => {
    const writesStyle = keepEmptyStyle
      ? styleContent !== undefined
      : !!styleContent;
    written(
      await updateVersion({
        variables: {
          where: { id },
          data: {
            htmlContent,
            ...(writesStyle ? { styleContent } : {}),
          },
        },
      }),
    );
    written(await detectSlots({ variables: { screenVersion: { id } } }));
  };

  const writeState = async ({ id, htmlContent }) => {
    written(
      await updateState({
        variables: { where: { id }, data: { htmlContent } },
      }),
    );
    written(await detectSlotsForState({ variables: { screenState: { id } } }));
  };

  /**
   * The id is the caller's, and `onCreated` fires between the two halves of
   * the gesture — after the row exists, before its slots are read.
   *
   * That seam is the only moment worth remembering. A failure in detection
   * leaves a state that exists and is wrongly mapped; the caller that knows
   * the row landed can retry with `writeState` instead of creating a second
   * one under a new id, which is what silently produced duplicates.
   */
  const createState = async ({
    id,
    screenVersionId,
    name,
    order,
    htmlContent,
    conditions,
    onCreated,
  }) => {
    const created = await addState({
      variables: {
        data: {
          id,
          name,
          order,
          htmlContent,
          conditions: conditions || [],
          screenVersion: { id: screenVersionId },
          bindingManifest: { id: cuid(), slots: [] },
        },
      },
    });
    written(created);
    if (onCreated) onCreated(id);
    written(await detectSlotsForState({ variables: { screenState: { id } } }));
  };

  return { writeVersion, writeState, createState };
};

export default useScreenWrites;
